import React from 'react';
import { Link } from 'react-router-dom';
import BlogArticleLayout, { blogStyles as s } from './BlogArticleLayout';

function BlogArticleSecuriteDonneesRGPD() {
  return (
    <BlogArticleLayout
      tag="RGPD"
      title="La sécurité des données selon le RGPD : ce que le texte impose vraiment"
      date="29 septembre 2026"
      ctaTitle="Vérifiez votre niveau de sécurité au regard du RGPD"
      ctaText="Le diagnostic gratuit évalue précisément vos mesures actuelles et identifie vos écarts par rapport aux exigences du RGPD."
    >
      <p style={s.intro}>
        L'expression « sécurité du traitement » revient dans toutes les discussions sur le RGPD, sans que son
        contenu concret soit toujours clair. Le texte n'impose pas une liste figée d'outils à installer : il
        impose un raisonnement, à appliquer à votre propre situation.
      </p>

      <h2 style={s.h2}>Une obligation de moyens proportionnés, pas une liste fixe</h2>
      <p>
        Le RGPD demande de mettre en place des mesures « appropriées » au regard de plusieurs facteurs : l'état
        de l'art, les coûts de mise en œuvre, la nature, la portée, le contexte et les finalités du traitement,
        ainsi que les risques pour les personnes concernées. Concrètement, cela veut dire qu'un cabinet médical
        traitant des données de santé et une petite boutique en ligne n'auront pas les mêmes obligations
        pratiques, même si le principe général est identique pour les deux.
      </p>

      <h2 style={s.h2}>Ce que couvrent concrètement les mesures attendues</h2>
      <p>Le texte cite explicitement plusieurs types de mesures, à adapter à votre contexte :</p>
      <ul style={s.ul}>
        <li style={s.li}>La pseudonymisation et le chiffrement des données personnelles.</li>
        <li style={s.li}>Des moyens garantissant la confidentialité, l'intégrité, la disponibilité et la résilience de vos systèmes.</li>
        <li style={s.li}>La capacité à rétablir la disponibilité et l'accès aux données rapidement en cas d'incident technique.</li>
        <li style={s.li}>Une procédure de test et d'évaluation régulière de l'efficacité de ces mesures — la sécurité n'est pas un état figé, mais un contrôle continu.</li>
      </ul>

      <h2 style={s.h2}>L'obligation de notification en cas de violation</h2>
      <p>
        En cas de violation de données personnelles (perte, accès non autorisé, divulgation accidentelle), le
        responsable de traitement doit la notifier à la CNIL dans un délai de 72 heures après en avoir pris
        connaissance, sauf si la violation n'est pas susceptible d'engendrer de risque pour les personnes
        concernées. Selon la gravité, les personnes concernées elles-mêmes doivent parfois également être
        informées.
      </p>

      <h2 style={s.h2}>Le principe de responsabilité (accountability)</h2>
      <p>
        Le RGPD ne se contente pas d'exiger des mesures : il exige de pouvoir prouver qu'elles existent et
        qu'elles sont adaptées. Documenter vos choix (pourquoi telle mesure plutôt qu'une autre, comment le
        risque a été évalué) fait partie intégrante de la conformité, au même titre que la mesure elle-même.
      </p>

      <h2 style={s.h2}>RGPD et cybersécurité : un socle commun, pas un doublon</h2>
      <p>
        Ces exigences de sécurité recoupent largement les bonnes pratiques de cybersécurité générales décrites
        dans notre
        {' '}<Link to="/blog/cybersecurite-pme-guide-dirigeants" style={s.link}>guide cybersécurité PME</Link>{' '}
        et dans notre article sur la
        {' '}<Link to="/blog/gestion-des-risques-cybersecurite-methode-commune" style={s.link}>gestion des risques commune à RGPD, NIS2 et ISO 27001</Link>.
        Traiter la sécurité RGPD isolément du reste de votre démarche cybersécurité conduit souvent à
        dupliquer des efforts qui pourraient être mutualisés.
      </p>

      <p style={s.disclaimer}>
        Cet article a une vocation informative et ne remplace pas une analyse juridique personnalisée de votre
        situation par un professionnel du droit ou votre DPO.
      </p>
    </BlogArticleLayout>
  );
}

export default BlogArticleSecuriteDonneesRGPD;
